/* src/components/admin/DispatcherVehicleModal.tsx */
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import {
    adminDispatcherVehicleService,
    DispatcherVehicle,
    CreateDispatcherVehicleData,
    UpdateDispatcherVehicleData
} from "../../services/admin.dispatcher.vehicle.service";
import { errorService } from "../../services/error.service";
import useForm, { ValidationRules } from "../../hooks/useForm";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faCar } from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/components/DispatcherVehicleModal.module.css";
import modalStyles from "../../styles/common/Modal.module.css";

interface DispatcherVehicleModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    idDispatcher: number;
    vehicle?: DispatcherVehicle | null;
}

interface DispatcherVehicleFormState {
    name: string;
    description: string;
}

const DispatcherVehicleModal: React.FC<DispatcherVehicleModalProps> = ({
    show,
    onHide,
    onSuccess,
    idDispatcher,
    vehicle
}) => {
    const [loading, setLoading] = useState(false);

    const isEditMode = !!vehicle;

    const initialValues = React.useMemo<DispatcherVehicleFormState>(() => ({
        name: vehicle?.name || "",
        description: vehicle?.description || "",
    }), [vehicle]);

    const validationRules = React.useMemo<ValidationRules<DispatcherVehicleFormState>>(() => ({
        name: (val) => (!val?.trim() ? "El nombre es obligatorio" : null),
    }), []);

    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        resetForm,
        setValues
    } = useForm<DispatcherVehicleFormState>(
        initialValues,
        validationRules
    );

    useEffect(() => {
        if (show) {
            setValues({
                name: vehicle?.name || "",
                description: vehicle?.description || "",
            });
        } else {
            resetForm();
        }
    }, [show, vehicle, resetForm, setValues]);

    const onSubmit = async () => {
        try {
            setLoading(true);

            if (isEditMode && vehicle) {
                const payload: UpdateDispatcherVehicleData = {
                    id: vehicle.id,
                    idDispatcher: idDispatcher,
                    name: values.name.trim(),
                    description: values.description.trim(),
                    status: 1, // Siempre activo
                };

                const response = await adminDispatcherVehicleService.updateDispatcherVehicle(payload);

                if (response.statusCode === 200) {
                    onHide();
                    onSuccess();
                    await Swal.fire({
                        icon: "success",
                        title: "¡Éxito!",
                        text: "El vehículo ha sido actualizado correctamente.",
                        confirmButtonText: "Aceptar",
                        confirmButtonColor: "var(--color-primary)",
                    });
                } else {
                    errorService.showErrorAlert(response, "Error al actualizar vehículo");
                }
            } else {
                const payload: CreateDispatcherVehicleData = {
                    idDispatcher: idDispatcher,
                    name: values.name.trim(),
                    description: values.description.trim(),
                    status: 1, // Siempre activo
                };

                const response = await adminDispatcherVehicleService.createDispatcherVehicle(payload);

                if (response.statusCode === 200) {
                    onHide();
                    onSuccess();
                    await Swal.fire({
                        icon: "success",
                        title: "¡Éxito!",
                        text: "El vehículo ha sido registrado correctamente.",
                        confirmButtonText: "Aceptar",
                        confirmButtonColor: "var(--color-primary)",
                    });
                } else {
                    errorService.showErrorAlert(response, "Error al registrar vehículo");
                }
            }
        } catch (error: any) {
            console.error("Error in DispatcherVehicleModal:", error);
            errorService.showErrorAlert(error, "Error de Conexión");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            className={styles.createDispatcherModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    {isEditMode ? "Editar Vehículo" : "Registrar Vehículo"}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit(onSubmit)} id="vehicle-form" noValidate>
                <Modal.Body>
                    <p className={styles.modalSubtitle}>
                        {isEditMode ? "Modifica los detalles del vehículo a continuación." : "Completa los datos del vehículo para registrarlo."}
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label>Nombre</Form.Label>
                        <div className={styles.inputWrapper}>
                            <FontAwesomeIcon icon={faCar} className={styles.inputIcon} />
                            <Form.Control
                                type="text"
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                className={`${styles.createDispatcherModalInput} ${errors.name ? styles.inputError : ""}`}
                                placeholder="Ej. Chevrolet Spark (AB123CD)"
                                disabled={loading}
                                maxLength={50}
                            />
                        </div>
                        {errors.name && <div className={styles.errorText}>{errors.name}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Descripción</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            className={styles.textareaInput}
                            placeholder="Detalles adicionales del vehículo..."
                            disabled={loading}
                            maxLength={256}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className={styles.modalFooter}>
                    <Button
                        variant="secondary"
                        onClick={onHide}
                        className={styles.secondaryBtn}
                        disabled={loading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        form="vehicle-form"
                        className={modalStyles.closeBtn}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} className="me-2" />
                                {isEditMode ? "Guardar Cambios" : "Registrar"}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default DispatcherVehicleModal;
